import React, { useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    User,
    Mail,
    Phone,
    MessageSquare,
    MapPin,
    Clock,
    Send,
    Facebook,
    Instagram,
    Linkedin,
    Youtube,
    Share2,
    Building,
    Loader,
    Twitter,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { ContactMetaData } from '../layouts/MetaData/ContactMetaData';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [storeOpen, setStoreOpen] = useState({
        seegehalli: true,
        bhuvaneshwari: true,
        kengeri: true
    });

    const toggleStore = (key) => {
        setStoreOpen(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Special handling for phone number
        if (name === 'phone') {
            // Only allow digits and limit to 10 characters
            const sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({
                ...prev,
                [name]: sanitizedValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            toast.error('Please enter your name');
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim() || !emailRegex.test(formData.email)) {
            toast.error('Please enter a valid email address');
            return false;
        }
        // Updated phone validation to ensure exactly 10 digits
        if (!formData.phone.trim() || formData.phone.length !== 10 || !/^[6-9]\d{9}$/.test(formData.phone)) {
            toast.error('Please enter a valid 10-digit mobile number starting with 6-9');
            return false;
        }
        if (!formData.message.trim()) {
            toast.error('Please enter your message');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        setLoading(true);
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json'
                },
                withCredentials: true
            };
            const response = await axios.post('https://meatbuddy.in/api/v1/contact/submit', 
                formData,
                config
            );
            if (response.data.success) {
                toast.success('Message sent successfully! We will get back to you soon.');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    message: ''
                });
            }
        } catch (error) {
            console.error('Contact form error:', error);
            
            if (error.response) {
                const errorMessage = error.response.data.message || 'Server error. Please try again.';
                toast.error(errorMessage);
            } else if (error.request) {
                toast.error('Unable to reach the server. Please check your internet connection.');
            } else {
                toast.error('An error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const socialLinks = [
        { icon: <Facebook className="w-5 h-5" />, url: 'https://www.facebook.com/profile.php?id=61573728508355', label: 'Facebook' },
        { icon: <Instagram className="w-5 h-5" />, url: 'https://www.instagram.com/meatbuddy.store/?hl=en', label: 'Instagram' },
        { icon: <Twitter className="w-5 h-5" />, url: 'https://x.com/Meat_Buddy', label: 'Twitter' },
        { icon: <Linkedin className="w-5 h-5" />, url: 'https://www.linkedin.com/in/meat-buddy-8b4666353/', label: 'LinkedIn' },
        { icon: <Youtube className="w-5 h-5" />, url: 'https://www.youtube.com/channel/UCu4ag1jOQe0ig01I7Z2PhFg', label: 'YouTube' },
        { icon: <Share2 className="w-5 h-5" />, url: 'https://in.pinterest.com/meatbuddyofficial/', label: 'Pinterest' }
    ];

    return (
        <>
            <ContactMetaData />
            <div
                className="py-8 px-4 sm:px-6 lg:px-8"
                style={{
                    backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="max-w-7xl mx-auto mb-8 text-center">
                    <h1 className="text-3xl font-bold text-[#dc2626] mb-4 font-['Crimson_Text']">Get in Touch</h1>
                    <p className="text-gray-600 max-w-2xl mx-auto text-base">
                        Need help or have questions? We're here for you! Contact us through any of these options.
                    </p>
                </div>

                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Contact Form - Left Side */}
                        <div className="lg:col-span-5">
                            <div className="bg-white rounded-xl shadow-sm p-6 lg:p-8 transform transition-all duration-300 hover:shadow-lg">
                                <h2 className="text-2xl font-semibold text-gray-800 mb-6 font-['Crimson_Text']">Send us a Message</h2>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <User className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="pl-10 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-[#dc2626] focus:border-[#dc2626] sm:text-sm h-11 text-center placeholder:text-center"
                                                required
                                                placeholder="Your Name"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Mail className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="pl-10 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-[#dc2626] focus:border-[#dc2626] sm:text-sm h-11 text-center placeholder:text-center"
                                                required
                                                placeholder="Your Email"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Phone className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="pl-10 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-[#dc2626] focus:border-[#dc2626] sm:text-sm h-11 text-center placeholder:text-center"
                                                required
                                                placeholder="Enter your 10 digit mobile number"
                                                pattern="[6-9][0-9]{9}"
                                                maxLength="10"
                                                inputMode="numeric"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                        <div className="relative">
                                            <div className="absolute top-3 left-3 pointer-events-none">
                                                <MessageSquare className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <textarea
                                                id="message"
                                                name="message"
                                                rows="4"
                                                value={formData.message}
                                                onChange={handleChange}
                                                className="pl-10 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-[#dc2626] focus:border-[#dc2626] sm:text-sm text-center placeholder:text-center"
                                                required
                                                placeholder="Your Message"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full flex justify-center items-center gap-2 px-4 py-2 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                    >
                                        {loading ? (
                                            <Loader className="h-5 w-5 animate-spin" />
                                        ) : (
                                            <>
                                                <Send className="h-5 w-5" />
                                                Send Message
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>

                        {/* Right Side */}
                        <div className="space-y-6 lg:col-span-7">
                            {/* Contact Information & Social Media - Wide Card with Single Line */}
                            <div className="bg-white rounded-xl shadow-sm p-6 transform transition-all duration-300 hover:shadow-lg">
                                <h2 className="text-xl font-semibold text-gray-800 mb-6 font-['Crimson_Text']">Contact Information</h2>
                                
                                {/* Contact info in a single row - wide format */}
                                <div className="flex flex-wrap items-center justify-around gap-4">
                                    {/* Phone */}
                                    <div className="flex items-center space-x-3">
                                        <Phone className="h-5 w-5 text-[#dc2626]" />
                                        <div>
                                            <h3 className="font-semibold text-gray-800 text-sm">Phone</h3>
                                            <a href="tel:+916364749404" className="text-gray-600 hover:text-[#dc2626] transition-colors text-sm">
                                                +91 6364749404
                                            </a>
                                        </div>
                                    </div>
                                    
                                    {/* Email */}
                                    <div className="flex items-center space-x-3">
                                        <Mail className="h-5 w-5 text-[#dc2626]" />
                                        <div>
                                            <h3 className="font-semibold text-gray-800 text-sm">Email</h3>
                                            <a href="mailto:customersupport@meatbuddy.in" className="text-gray-600 hover:text-[#dc2626] transition-colors text-sm">
                                                customersupport@meatbuddy.in
                                            </a>
                                        </div>
                                    </div>
                                    
                                    {/* Social Media Icons */}
                                    <div>
                                        <h3 className="font-semibold text-gray-800 text-sm mb-2">Connect With Us</h3>
                                        <div className="flex space-x-3">
                                            {socialLinks.map((social, index) => (
                                                <a
                                                    key={index}
                                                    href={social.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`Visit our ${social.label} page`}
                                                    className="p-2 bg-gray-100 rounded-lg hover:bg-[#dc2626] hover:text-white transition-all duration-300"
                                                >
                                                    {social.icon}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Shop Locations */}
                            <div className="bg-white rounded-xl shadow-sm p-6 transform transition-all duration-300 hover:shadow-lg">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4 font-['Crimson_Text']">Shop Locations</h2>
                                <div className="space-y-4">
                                    {/* Store - Seegehalli */}
                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => toggleStore('seegehalli')}
                                            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <MapPin className="h-5 w-5 text-[#dc2626] flex-shrink-0" />
                                                <h3 className="font-semibold text-gray-800 text-sm">Store - Seegehalli</h3>
                                            </div>
                                            {storeOpen.seegehalli ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
                                        </button>
                                        {storeOpen.seegehalli && (
                                            <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                                                <p className="text-gray-600 text-sm pt-3">
                                                    Shop No 63, Seegehalli,<br />
                                                    Bengaluru, Seegehalli,<br />
                                                    Karnataka 562130
                                                </p>
                                                <div className="mt-2">
                                                    <h4 className="font-semibold text-gray-800 mb-1 text-sm">Store Hours</h4>
                                                    <p className="text-gray-600 text-sm">Tuesday - Sunday: 7:00 AM - 9:00 PM</p>
                                                </div>
                                                <div className="mt-4 h-[200px] rounded-lg overflow-hidden">
                                                    <iframe
                                                        title="Meat Buddy Store - Seegehalli"
                                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.8374540796153!2d77.44130217358925!3d12.982246214647793!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3b988b171d5b%3A0xec0049ca933af3a8!2sMeat%20Buddy!5e0!3m2!1sen!2sin!4v1772028009242!5m2!1sen!2sin"
                                                        width="100%"
                                                        height="100%"
                                                        style={{ border: 0 }}
                                                        allowFullScreen=""
                                                        loading="lazy"
                                                        referrerPolicy="no-referrer-when-downgrade"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* Store - Bhuvaneshwari Nagar */}
                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => toggleStore('bhuvaneshwari')}
                                            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <MapPin className="h-5 w-5 text-[#dc2626] flex-shrink-0" />
                                                <h3 className="font-semibold text-gray-800 text-sm">Store - Bhuvaneshwari Nagar</h3>
                                            </div>
                                            {storeOpen.bhuvaneshwari ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
                                        </button>
                                        {storeOpen.bhuvaneshwari && (
                                            <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                                                <p className="text-gray-600 text-sm pt-3">
                                                    2nd Stage, Bhuvaneshwari Nagar,<br />
                                                    Nagdevanahalli, Bengaluru,<br />
                                                    Karnataka 560056
                                                </p>
                                                <div className="mt-2">
                                                    <h4 className="font-semibold text-gray-800 mb-1 text-sm">Store Hours</h4>
                                                    <p className="text-gray-600 text-sm">Tuesday - Sunday: 7:00 AM - 9:00 PM</p>
                                                </div>
                                                <div className="mt-4 h-[200px] rounded-lg overflow-hidden">
                                                    <iframe
                                                        title="Meat Buddy Store - Bhuvaneshwari Nagar"
                                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.455518572712!2d77.48453797358847!3d12.942677715514689!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3f39a70b5bfd%3A0x376282013769bb4e!2sMeatbuddy-Bhuvaneshwari%20Nagara!5e0!3m2!1sen!2sin!4v1772027653354!5m2!1sen!2sin"
                                                        width="100%"
                                                        height="100%"
                                                        style={{ border: 0 }}
                                                        allowFullScreen=""
                                                        loading="lazy"
                                                        referrerPolicy="no-referrer-when-downgrade"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* Store - Kengeri */}
                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => toggleStore('kengeri')}
                                            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <MapPin className="h-5 w-5 text-[#dc2626] flex-shrink-0" />
                                                <h3 className="font-semibold text-gray-800 text-sm">Store - Kengeri</h3>
                                            </div>
                                            {storeOpen.kengeri ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
                                        </button>
                                        {storeOpen.kengeri && (
                                            <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                                                <p className="text-gray-600 text-sm pt-3">
                                                    15/1, 8th A Cross Rd,<br />
                                                    Jnanabharathi Extension, Volagalahalli,<br />
                                                    Stage II, Kengeri Satellite Town,<br />
                                                    Bengaluru, Karnataka 560060
                                                </p>
                                                <div className="mt-2">
                                                    <h4 className="font-semibold text-gray-800 mb-1 text-sm">Store Hours</h4>
                                                    <p className="text-gray-600 text-sm">Tuesday - Sunday: 7:00 AM - 9:00 PM</p>
                                                </div>
                                                <div className="mt-4 h-[200px] rounded-lg overflow-hidden">
                                                    <iframe
                                                        title="Meat Buddy Store - Kengeri"
                                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.7464616748016!2d77.48475547358812!3d12.924010315922873!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3f83a990952d%3A0xed5d470cc8fdae49!2sMeat%20Buddy%20(Hoysala%20Circle)!5e0!3m2!1sen!2sin!4v1772028093987!5m2!1sen!2sin"
                                                        width="100%"
                                                        height="100%"
                                                        style={{ border: 0 }}
                                                        allowFullScreen=""
                                                        loading="lazy"
                                                        referrerPolicy="no-referrer-when-downgrade"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Contact;